import React, { useState } from 'react';
import styled, { css } from 'styled-components';

interface Pagination {
  page: number;
  PaginationBar: React.FC;
}

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const buttonBase = css`
  border: 1px solid #ccc;
  background-color: white;
  color: #333;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background-color: #f5f5f5;
    color: #646ea0;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const PageButton = styled.button<{ $active?: boolean }>`
  ${buttonBase};
  ${({ $active }) =>
    $active &&
    css`
      background-color: #646ea0;
      color: white;
      border-color: #646ea0;
      font-weight: 600;
    `}
`;

const NavButton = styled.button`
  ${buttonBase};
`;

const Ellipsis = styled.span`
  padding: 0 8px;
  display: flex;
  align-items: center;
`;

export const usePagination = (totalPages: number): Pagination => {
  const [page, setPage] = useState<number>(1);
  const nextPage = () => setPage(p => Math.min(p + 1, totalPages));
  const prevPage = () => setPage(p => Math.max(p - 1, 1));
  const goToPage = (page: number) =>
    setPage(Math.min(Math.max(page, 1), totalPages));

  const getPageNumbers = (): (number | string)[] => {
    const delta = 2; // Number of pages to show on each side of current page
    const pages: (number | string)[] = [];

    pages.push(1);

    const rangeStart = Math.max(2, page - delta);
    const rangeEnd = Math.min(totalPages - 1, page + delta);

    if (rangeStart > 2) {
      pages.push('...');
    }

    for (let i = rangeStart; i <= rangeEnd; i += 1) {
      pages.push(i);
    }

    if (rangeEnd < totalPages - 1) {
      pages.push('...');
    }

    if (totalPages > 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const PaginationBar: React.FC = () => (
    <PaginationContainer>
      <NavButton onClick={prevPage} disabled={page === 1}>
        Prev
      </NavButton>
      {getPageNumbers().map((p, idx) => {
        if (p === '...') {
          const i = idx;
          return <Ellipsis key={`ellipsis-${i}`}>...</Ellipsis>;
        }
        const pageNum = p as number;
        const isActive = pageNum === page;
        return (
          <PageButton
            key={pageNum}
            onClick={() => goToPage(pageNum)}
            $active={isActive}
          >
            {pageNum}
          </PageButton>
        );
      })}
      <NavButton onClick={nextPage} disabled={page === totalPages}>
        Next
      </NavButton>
    </PaginationContainer>
  );

  return { page, PaginationBar };
};
